import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/core';
import { useEffect, useRef, useState } from 'react';
import { renderLatexToHtml } from '../lib/latex';

/** Inline component: splits text on $...$ and renders math in-place. */
export function LatexInline({ text }: { text: string }) {
  const parts = text.split(/\$([^$\n]+)\$/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 0
          ? <span key={i}>{part}</span>
          : <span key={i} dangerouslySetInnerHTML={{ __html: renderLatexToHtml(part) }} />
      )}
    </>
  );
}

/** NodeView for inlineMath: selected → raw $...$ input; deselected → KaTeX render. */
export function InlineMathView({ node, updateAttributes, selected }: NodeViewProps) {
  const tex = (node.attrs as { content: string }).content;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selected) inputRef.current?.focus();
  }, [selected]);

  return (
    <NodeViewWrapper as="span" className="math-nv-inline">
      {selected ? (
        <span className="math-editing-inline">
          <span className="math-delim">$</span>
          <input
            ref={inputRef}
            className="math-edit-input"
            value={tex}
            onChange={(e) => updateAttributes({ content: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Escape') (e.target as HTMLInputElement).blur(); }}
            size={Math.max(tex.length, 2)}
          />
          <span className="math-delim">$</span>
        </span>
      ) : (
        <span
          className="math-rendered-inline"
          dangerouslySetInnerHTML={{ __html: renderLatexToHtml(tex) || `$${tex}$` }}
        />
      )}
    </NodeViewWrapper>
  );
}

/** NodeView for blockMath: click → textarea for editing; default → KaTeX display. */
export function BlockMathView({ node, updateAttributes }: NodeViewProps) {
  const tex = (node.attrs as { content: string }).content;
  const [editing, setEditing] = useState(false);
  const [localTex, setLocalTex] = useState(tex);

  useEffect(() => { setLocalTex(tex); }, [tex]);

  const commit = () => {
    updateAttributes({ content: localTex });
    setEditing(false);
  };

  return (
    <NodeViewWrapper className="math-nv-block">
      {editing ? (
        <div className="math-block-editing">
          <textarea
            className="math-block-input"
            value={localTex}
            autoFocus
            rows={Math.max(localTex.split('\n').length, 1)}
            onChange={(e) => setLocalTex(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Escape') commit(); }}
          />
        </div>
      ) : (
        <div
          className="math-rendered-block"
          onClick={() => setEditing(true)}
          dangerouslySetInnerHTML={{ __html: renderLatexToHtml(tex, true) || `$$${tex}$$` }}
        />
      )}
    </NodeViewWrapper>
  );
}
